import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Loader2,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Eye,
  AlertTriangle,
  Tag,
  Calendar,
  User,
  FileText,
  Trash2,
  Scale,
  Circle
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const getTrademarkStatusBadge = (status, size = "normal") => {
  const sizeClasses = size === "small" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";
  switch (status) {
    case "registered":
      return <span className={`inline-flex items-center gap-1 ${sizeClasses} bg-emerald-100 text-emerald-700 border border-emerald-200 font-mono uppercase`}>Registered</span>;
    case "examination":
      return <span className={`inline-flex items-center gap-1 ${sizeClasses} bg-blue-100 text-blue-700 border border-blue-200 font-mono uppercase`}>Examination</span>;
    case "published":
      return <span className={`inline-flex items-center gap-1 ${sizeClasses} bg-purple-100 text-purple-700 border border-purple-200 font-mono uppercase`}>Published</span>;
    case "opposed":
      return <span className={`inline-flex items-center gap-1 ${sizeClasses} bg-orange-100 text-orange-700 border border-orange-200 font-mono uppercase`}>Opposed</span>;
    case "dead":
      return <span className={`inline-flex items-center gap-1 ${sizeClasses} bg-slate-100 text-slate-500 border border-slate-200 font-mono uppercase`}>Dead</span>;
    default:
      return null;
  }
};

export const AlertDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [alert, setAlert] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [alertRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/alerts/${id}`),
          axios.get(`${API}/categories`)
        ]);
        setAlert(alertRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error("Error fetching alert:", error);
        toast.error("Failed to load alert details");
        navigate('/alerts');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : `Class ${categoryId}`;
  };

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API}/alerts/${id}/status?status=${status}`);
      setAlert({ ...alert, status });
      toast.success("Status updated");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/alerts/${id}`);
      toast.success("Alert deleted");
      navigate('/alerts');
    } catch (error) {
      console.error("Error deleting alert:", error);
      toast.error("Failed to delete alert");
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "high": return "text-red-600";
      case "medium": return "text-orange-600";
      case "low": return "text-emerald-600";
      default: return "text-slate-600";
    }
  };

  const getRiskBg = (level) => {
    switch (level) {
      case "high": return "bg-red-50 border-red-200";
      case "medium": return "bg-orange-50 border-orange-200";
      case "low": return "bg-emerald-50 border-emerald-200";
      default: return "bg-slate-50 border-slate-200";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="alert-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!alert) {
    return null;
  }

  return (
    <div data-testid="alert-detail-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/alerts')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Alerts
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
              Alert Details
            </h1>
            <p className="text-slate-500 mt-1">Trademark conflict analysis</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowDelete(true)}
            className="btn-secondary text-red-600 hover:bg-red-50"
            data-testid="delete-alert-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            DELETE
          </Button>
        </div>
      </div>

      {/* Risk Summary */}
      <motion.div 
        className={`swiss-card ${getRiskBg(alert.overall_risk_level)} mb-6`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 flex items-center justify-center ${
              alert.overall_risk_level === 'high' ? 'bg-red-100' :
              alert.overall_risk_level === 'medium' ? 'bg-orange-100' :
              'bg-emerald-100'
            }`}>
              <ShieldAlert className={`w-8 h-8 ${getRiskColor(alert.overall_risk_level)}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500 uppercase tracking-wider">Overall Risk Level</p>
              <p className={`text-3xl font-heading font-black uppercase ${getRiskColor(alert.overall_risk_level)}`}>
                {alert.overall_risk_level}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={alert.status === "reviewed" ? "default" : "outline"}
              onClick={() => updateStatus("reviewed")}
              className={alert.status === "reviewed" ? "btn-primary" : "btn-secondary"}
              data-testid="mark-reviewed-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Reviewed
            </Button>
            <Button
              variant={alert.status === "actioned" ? "default" : "outline"}
              onClick={() => updateStatus("actioned")}
              className={alert.status === "actioned" ? "btn-primary" : "btn-secondary"}
              data-testid="mark-actioned-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Actioned
            </Button>
            <Button
              variant={alert.status === "dismissed" ? "default" : "outline"}
              onClick={() => updateStatus("dismissed")}
              className={alert.status === "dismissed" ? "btn-primary" : "btn-secondary"}
              data-testid="mark-dismissed-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Dismiss
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Comparison Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Your Trademark */}
        <motion.div 
          className="swiss-card"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
              Your Protected Trademark
            </span>
          </div>
          <h2 className="text-2xl font-heading font-black text-slate-900 mb-2">
            {alert.my_trademark_name}
          </h2>
          <div className="mb-4">
            {getTrademarkStatusBadge(alert.my_trademark_status)}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/trademarks`)}
            className="btn-secondary"
            data-testid="view-trademark-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            View Details
          </Button>
        </motion.div>

        {/* Conflicting Trademark */}
        <motion.div 
          className="swiss-card border-orange-200"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
              Conflicting Application
            </span>
          </div>
          <h2 className="text-2xl font-heading font-black text-slate-900 mb-2">
            {alert.conflicting_trademark_name}
          </h2>
          <div className="mb-4">
            {getTrademarkStatusBadge(alert.conflicting_trademark_status)}
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">{alert.conflicting_trademark_applicant}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="font-mono text-slate-600">
                {alert.conflicting_trademark_application_number}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">{alert.conflicting_trademark_filing_date}</span>
            </div>
          </div>
          {alert.conflicting_categories && alert.conflicting_categories.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Categories</p>
              <div className="flex flex-wrap gap-2">
                {alert.conflicting_categories.map(catId => (
                  <span 
                    key={catId}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-mono"
                  >
                    <Tag className="w-3 h-3" />
                    {getCategoryName(catId)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Similarity Analysis */}
      <motion.div 
        className="swiss-card mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center gap-2 mb-6">
          <Scale className="w-5 h-5 text-slate-600" />
          <h2 className="text-xl font-heading font-bold text-slate-900">Similarity Analysis</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Similarity */}
          <div className="p-4 bg-slate-50 border border-slate-200">
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">Basic Text Match</p>
            <div className="flex items-end gap-2">
              <span className={`text-4xl font-mono font-bold ${
                alert.basic_similarity_score >= 0.7 ? 'text-red-600' :
                alert.basic_similarity_score >= 0.5 ? 'text-orange-600' :
                'text-emerald-600'
              }`}>
                {Math.round(alert.basic_similarity_score * 100)}%
              </span>
              <span className="text-slate-400 text-sm mb-1">similarity</span>
            </div>
            <div className="mt-3 h-2 bg-slate-200 overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${
                  alert.basic_similarity_score >= 0.7 ? 'bg-red-500' :
                  alert.basic_similarity_score >= 0.5 ? 'bg-orange-500' :
                  'bg-emerald-500'
                }`}
                style={{ width: `${alert.basic_similarity_score * 100}%` }}
              />
            </div>
          </div>

          {/* AI Similarity */}
          <div className="p-4 bg-slate-50 border border-slate-200">
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">AI Semantic Match</p>
            {alert.ai_similarity_score !== null ? (
              <>
                <div className="flex items-end gap-2">
                  <span className={`text-4xl font-mono font-bold ${
                    alert.ai_similarity_score >= 0.7 ? 'text-red-600' :
                    alert.ai_similarity_score >= 0.5 ? 'text-orange-600' :
                    'text-emerald-600'
                  }`}>
                    {Math.round(alert.ai_similarity_score * 100)}%
                  </span>
                  <span className="text-slate-400 text-sm mb-1">similarity</span>
                </div>
                <div className="mt-3 h-2 bg-slate-200 overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      alert.ai_similarity_score >= 0.7 ? 'bg-red-500' :
                      alert.ai_similarity_score >= 0.5 ? 'bg-orange-500' :
                      'bg-emerald-500'
                    }`}
                    style={{ width: `${alert.ai_similarity_score * 100}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="text-slate-400">AI analysis not available</p>
            )}
          </div>
        </div>

        {/* AI Analysis */}
        {alert.ai_analysis && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200">
            <p className="text-sm text-blue-600 uppercase tracking-wider mb-2">AI Analysis</p>
            <p className="text-slate-700">{alert.ai_analysis}</p>
          </div>
        )}

        {/* Logo Similarity */}
        {alert.logo_similarity_score !== null && alert.logo_similarity_score !== undefined && (
          <div className="mt-6">
            <div className="p-4 bg-purple-50 border border-purple-200">
              <p className="text-sm text-purple-600 uppercase tracking-wider mb-2">Logo/Visual Similarity</p>
              <div className="flex items-end gap-2">
                <span className={`text-4xl font-mono font-bold ${
                  alert.logo_similarity_score >= 0.7 ? 'text-red-600' :
                  alert.logo_similarity_score >= 0.5 ? 'text-orange-600' :
                  'text-emerald-600'
                }`}>
                  {Math.round(alert.logo_similarity_score * 100)}%
                </span>
                <span className="text-slate-400 text-sm mb-1">visual similarity</span>
              </div>
              <div className="mt-3 h-2 bg-purple-200 overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${
                    alert.logo_similarity_score >= 0.7 ? 'bg-red-500' :
                    alert.logo_similarity_score >= 0.5 ? 'bg-orange-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${alert.logo_similarity_score * 100}%` }}
                />
              </div>
              {alert.logo_analysis && (
                <p className="text-slate-700 mt-3 text-sm">{alert.logo_analysis}</p>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Delete Confirmation */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent data-testid="delete-alert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Alert</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this alert? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
